import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Calendar, Trophy, BarChart3, FileText, Settings, Vote, Youtube } from 'lucide-react';
import SubscribeButton from '../components/SubscribeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@tratingpro';

// Extract YouTube video ID from any YouTube URL (watch, youtu.be, shorts, embed)
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
    /^([A-Za-z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const ReplaysPage = () => {
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReplays();
  }, []);

  const fetchReplays = async () => {
    try {
      const res = await axios.get(`${API}/replays`);
      setReplays(res.data?.replays || []);
    } catch (err) {
      console.error('Erro ao carregar replays:', err);
      setReplays([]);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-zinc-950 text-white py-4 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <a href="/" className="text-2xl sm:text-3xl font-black tracking-tighter hover:text-green-400 transition-colors">
            ⚽ DOMINGÃO
          </a>
          <div className="flex items-center gap-2">
            <SubscribeButton />
            <a href="/admin" className="text-sm font-bold hover:text-green-400 transition-colors" data-testid="go-to-admin">
              <Settings size={24} />
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b-2 border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <a href="/" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-home">HOME</a>
            <a href="/votar" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-vote">
              <Vote className="inline mr-1" size={16} /> VOTAR
            </a>
            <a href="/estatisticas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-stats">
              <BarChart3 className="inline mr-1" size={16} /> ESTATÍSTICAS
            </a>
            <a href="/torneios" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-torneios">
              <Trophy className="inline mr-1" size={16} /> TORNEIOS
            </a>
            <a href="/partidas" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-matches">
              <Calendar className="inline mr-1" size={16} /> PARTIDAS
            </a>
            <a href="/rankings" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rankings">
              <Trophy className="inline mr-1" size={16} /> RANKINGS
            </a>
            <a href="/replays" className="flex-shrink-0 bg-green-500 text-white px-4 py-2 rounded-lg font-bold text-sm" data-testid="nav-replays">
              <Youtube className="inline mr-1" size={16} /> REPLAYS
            </a>
            <a href="/regras" className="flex-shrink-0 bg-zinc-100 hover:bg-zinc-200 px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-testid="nav-rules">
              <FileText className="inline mr-1" size={16} /> REGRAS
            </a>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase flex items-center gap-2">
            <Youtube size={32} className="text-red-600" /> REPLAYS
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Vê de novo os melhores momentos das nossas partidas no YouTube.</p>
        </div>

        {/* Channel CTA — always visible */}
        <a
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-6 flex items-center justify-between gap-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-2xl p-4 sm:p-5 shadow-md transition-all"
          data-testid="youtube-channel-cta"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <Youtube size={28} strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider opacity-80">Canal oficial no YouTube</p>
              <p className="text-base sm:text-lg font-black truncate">@tratingpro · Ver todas as partidas</p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 bg-white text-red-600 px-4 py-2 rounded-full font-black text-sm flex-shrink-0">
            ABRIR CANAL →
          </span>
          <span className="sm:hidden inline-flex items-center gap-1 bg-white text-red-600 px-3 py-1.5 rounded-full font-black text-xs flex-shrink-0">
            ABRIR →
          </span>
        </a>

        {loading ? (
          <div className="text-center py-12 text-zinc-400 font-bold">A carregar…</div>
        ) : replays.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-zinc-200 p-8 text-center" data-testid="replays-empty">
            <Youtube size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-xl font-bold text-zinc-400 mb-2">Sem replays publicados aqui ainda</p>
            <p className="text-zinc-500 mb-5">Mas as partidas anteriores estão no nosso canal do YouTube — clica em cima para veres tudo. 👆</p>
            <a
              href={YOUTUBE_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-black text-sm transition-colors"
              data-testid="empty-channel-btn"
            >
              <Youtube size={18} /> Ir para o canal
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {replays.map((replay) => {
              const videoId = extractYouTubeId(replay.youtube_url);
              return (
                <article
                  key={replay.id}
                  className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden hover:border-zinc-400 transition-colors"
                  data-testid={`replay-card-${replay.id}`}
                >
                  <div className="relative aspect-video bg-black">
                    {videoId ? (
                      <iframe
                        title={`Replay ${replay.date}`}
                        src={`https://www.youtube.com/embed/${videoId}`}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-zinc-400">
                        <Play size={48} />
                        <span className="ml-2 text-sm">URL inválido</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-zinc-500">
                      {formatDate(replay.date)}
                    </p>
                    <p className="text-base font-black tracking-tight mt-1">
                      {replay.location || 'Partida do Domingão'}
                    </p>
                    {videoId && (
                      <a
                        href={replay.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700 mt-2"
                      >
                        <Youtube size={14} /> Abrir no YouTube
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ReplaysPage;
